import CellPrice from '@/components/GokuDataGrid/CellPrice'
import type {
    InvoiceRevenueAllocation,
    InvoiceType,
} from '@/modules/Report/components/Revenue/types'
import { ChevronDown, ChevronRight } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { Chip } from 'vnetwork-ui'

interface MonthlyGroupedViewProps {
    allocations: InvoiceRevenueAllocation[]
    t: (key: string) => string
}

interface MonthlyItem {
    invoice_item_id: number
    product_group_name: string
    billing_period: string
    allocated_amount: number
    revenue_type: InvoiceType
    is_recognized: boolean
}

interface MonthGroup {
    revenue_month: string
    total_amount: number
    items: MonthlyItem[]
}

interface ProductGroupData {
    product_group_name: string
    total_amount: number
    months: MonthGroup[]
}

/**
 * Revenue allocation grouped by month view.
 * Level 1: product group (skipped if all items share same group)
 * Level 2: month (items with same month & year grouped together)
 */
const MonthlyGroupedView: React.FC<MonthlyGroupedViewProps> = ({
    allocations,
    t,
}) => {
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
    const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set())

    // Flatten all monthly allocations and group by product_group → month
    const { groups, hasSingleGroup } = useMemo(() => {
        const flatItems: (MonthlyItem & { revenue_month: string })[] = []

        allocations.forEach((alloc) => {
            const billingPeriod = `${alloc.billing_period_start.split('T')[0]} - ${alloc.billing_period_end.split('T')[0]}`
            alloc.revenue_allocations?.forEach((ma) => {
                flatItems.push({
                    invoice_item_id: alloc.invoice_item_id,
                    product_group_name: alloc.product_group_name,
                    billing_period: billingPeriod,
                    allocated_amount: ma.allocated_amount,
                    revenue_type: ma.revenue_type,
                    is_recognized: ma.is_recognized,
                    revenue_month: ma.revenue_month,
                })
            })
        })

        const uniqueGroups = [
            ...new Set(flatItems.map((i) => i.product_group_name)),
        ]
        const hasSingleGroup = uniqueGroups.length <= 1

        // Group: product_group_name → revenue_month → items[]
        const groupMap = new Map<string, Map<string, MonthlyItem[]>>()

        flatItems.forEach((item) => {
            if (!groupMap.has(item.product_group_name)) {
                groupMap.set(item.product_group_name, new Map())
            }
            const monthMap = groupMap.get(item.product_group_name)!
            if (!monthMap.has(item.revenue_month)) {
                monthMap.set(item.revenue_month, [])
            }
            monthMap.get(item.revenue_month)!.push(item)
        })

        const groups: ProductGroupData[] = [...groupMap.entries()].map(
            ([pgName, monthMap]) => {
                const months: MonthGroup[] = [...monthMap.entries()]
                    .sort((a, b) => a[0].localeCompare(b[0]))
                    .map(([month, items]) => ({
                        revenue_month: month,
                        total_amount: items.reduce(
                            (sum, i) => sum + i.allocated_amount,
                            0
                        ),
                        items,
                    }))

                return {
                    product_group_name: pgName,
                    total_amount: months.reduce(
                        (sum, m) => sum + m.total_amount,
                        0
                    ),
                    months,
                }
            }
        )

        return { groups, hasSingleGroup }
    }, [allocations])

    const toggleGroup = (name: string) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev)
            next.has(name) ? next.delete(name) : next.add(name)
            return next
        })
    }

    const toggleMonth = (key: string) => {
        setExpandedMonths((prev) => {
            const next = new Set(prev)
            next.has(key) ? next.delete(key) : next.add(key)
            return next
        })
    }

    if (groups.length === 0) {
        return (
            <div className="text-center py-8 text-gray-500">
                {t('no_data') || 'No data'}
            </div>
        )
    }

    // Render item detail rows inside an expanded month
    const renderItemRows = (items: MonthlyItem[], monthKey: string) =>
        items.map((item, idx) => (
            <tr key={`${monthKey}-${idx}`} className="bg-gray-50/50">
                <td className="px-2 py-1.5 w-10" />
                <td className="px-4 py-1.5 pl-12 text-xs text-gray-600">
                    {item.billing_period}
                </td>
                <td className="px-4 py-1.5 text-xs text-right text-gray-700">
                    <CellPrice price={item.allocated_amount} />
                </td>
                <td className="px-4 py-1.5 text-center">
                    <Chip
                        label={
                            t(`invoice_type.${item.revenue_type}`) ||
                            item.revenue_type
                        }
                        color={
                            item.revenue_type === 'renewal'
                                ? 'primary'
                                : item.revenue_type === 'new_sale'
                                  ? 'success'
                                  : 'warning'
                        }
                        size="small"
                    />
                </td>
                <td className="px-4 py-1.5 text-center">
                    <Chip
                        label={
                            item.is_recognized
                                ? t('recognized') || 'Recognized'
                                : t('unrecognized') || 'Unrecognized'
                        }
                        color={item.is_recognized ? 'success' : 'default'}
                        size="small"
                    />
                </td>
            </tr>
        ))

    // Render month rows with expand/collapse for item details
    const renderMonthRows = (
        months: MonthGroup[],
        groupPrefix: string,
        indented = false
    ) =>
        months.map((month) => {
            const monthKey = `${groupPrefix}:${month.revenue_month}`
            const isExpanded = expandedMonths.has(monthKey)
            const plClass = indented ? 'pl-10' : ''

            return (
                <React.Fragment key={monthKey}>
                    <tr
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => toggleMonth(monthKey)}
                    >
                        <td className="px-2 py-2.5 w-10">
                            {month.items.length > 1 && (
                                <button className="text-gray-400 hover:text-gray-600">
                                    {isExpanded ? (
                                        <ChevronDown className="w-4 h-4" />
                                    ) : (
                                        <ChevronRight className="w-4 h-4" />
                                    )}
                                </button>
                            )}
                        </td>
                        <td
                            className={`px-4 py-2.5 text-sm font-medium text-gray-900 ${plClass}`}
                        >
                            {month.revenue_month}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-right font-semibold text-gray-900">
                            <CellPrice price={month.total_amount} />
                        </td>
                        <td className="px-4 py-2.5 text-center text-xs text-gray-500">
                            {month.items.length} {t('items') || 'items'}
                        </td>
                        <td className="px-4 py-2.5" />
                    </tr>
                    {isExpanded && renderItemRows(month.items, monthKey)}
                </React.Fragment>
            )
        })

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="max-h-[60vh] overflow-y-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                        <tr>
                            <th className="px-2 py-3 w-10" />
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {hasSingleGroup
                                    ? t('revenue_month') || 'Month'
                                    : t('product_group') || 'Product Group'}
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('allocated_amount') || 'Amount'}
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('details') || 'Details'}
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('status') || 'Status'}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {hasSingleGroup
                            ? // Single group → show months directly
                              renderMonthRows(
                                  groups[0].months,
                                  groups[0].product_group_name
                              )
                            : // Multiple groups → product group level first
                              groups.map((group) => {
                                  const isGroupExpanded = expandedGroups.has(
                                      group.product_group_name
                                  )
                                  return (
                                      <React.Fragment
                                          key={group.product_group_name}
                                      >
                                          <tr
                                              className="hover:bg-blue-50/50 cursor-pointer bg-blue-50/20"
                                              onClick={() =>
                                                  toggleGroup(
                                                      group.product_group_name
                                                  )
                                              }
                                          >
                                              <td className="px-2 py-3 w-10">
                                                  <button className="text-gray-500 hover:text-gray-700">
                                                      {isGroupExpanded ? (
                                                          <ChevronDown className="w-4 h-4" />
                                                      ) : (
                                                          <ChevronRight className="w-4 h-4" />
                                                      )}
                                                  </button>
                                              </td>
                                              <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                                                  {group.product_group_name}
                                              </td>
                                              <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">
                                                  <CellPrice
                                                      price={group.total_amount}
                                                  />
                                              </td>
                                              <td className="px-4 py-3 text-center text-xs text-gray-500">
                                                  {group.months.length}{' '}
                                                  {t('months') || 'months'}
                                              </td>
                                              <td className="px-4 py-3" />
                                          </tr>
                                          {isGroupExpanded &&
                                              renderMonthRows(
                                                  group.months,
                                                  group.product_group_name,
                                                  true
                                              )}
                                      </React.Fragment>
                                  )
                              })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default MonthlyGroupedView
